import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';
const storageKey = 'payment-theme';

function savedTheme(): Theme | null {
    try {
        const value = localStorage.getItem(storageKey);
        return value === 'light' || value === 'dark' ? value : null;
    } catch {
        return null;
    }
}

function systemTheme(): Theme {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

const ThemeContext = createContext<{ theme: Theme; toggleTheme: () => void }>({
    theme: 'light',
    toggleTheme: () => {},
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    const [preference, setPreference] = useState<Theme | null>(savedTheme);
    const [system, setSystem] = useState<Theme>(systemTheme);
    const theme = preference ?? system;

    useEffect(() => {
        const media = window.matchMedia?.('(prefers-color-scheme: dark)');
        const updateSystem = () => setSystem(systemTheme());
        const updatePreference = (event: StorageEvent) => {
            if (event.key === storageKey || event.key === null) setPreference(savedTheme());
        };
        media?.addEventListener('change', updateSystem);
        window.addEventListener('storage', updatePreference);
        return () => {
            media?.removeEventListener('change', updateSystem);
            window.removeEventListener('storage', updatePreference);
        };
    }, []);

    useEffect(() => {
        document.documentElement.classList.toggle('dark', theme === 'dark');
        document.documentElement.style.colorScheme = theme;
        document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'dark' ? '#0f172a' : '#ffffff');
    }, [theme]);

    const toggleTheme = () => {
        const next = theme === 'dark' ? 'light' : 'dark';
        setPreference(next);
        try {
            localStorage.setItem(storageKey, next);
        } catch {
            // The switch still works when browser storage is unavailable.
        }
    };

    return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
